import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import { listChatbots, createChatbot, deleteChatbot, duplicateChatbot } from 'src/actions/v1/chatbots/v1_actionChatbots';
import useAuth from 'src/hooks/useAuth';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import { CHATBOTS_DESIGN as D } from './Chatbots.design';

// ----------------------------------------------------------------------

export default function ChatbotList() {
    const { user } = useAuth();
    const [chatbots, setChatbots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [chatbotIdToDelete, setChatbotIdToDelete] = useState(null);
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation('settings');

    const fetchChatbots = async () => {
        try {
            setLoading(true);
            const response = await listChatbots(user.clienteId);
            setChatbots(response.data.data);
        } catch (error) {
            enqueueSnackbar(t('chatbotSettings.messages.fetchError', 'Erro ao buscar chatbots'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user?.clienteId) {
            fetchChatbots();
        }
    }, [user]);

    const handleCreate = async () => {
        try {
            const newBot = {
                name: t('chatbotSettings.newChatbotLabel', 'Novo Chatbot'),
                description: t('chatbotSettings.defaultDescription', 'Descrição do chatbot'),
                clienteId: user.clienteId,
                flowData: { nodes: [], edges: [] },
            };
            const response = await createChatbot(newBot);
            setChatbots([...chatbots, response.data.data]);
            enqueueSnackbar(t('chatbotSettings.messages.createSuccess', 'Chatbot criado com sucesso'), { variant: 'success' });
            navigate(`/dashboard/chatbots/builder/${response.data.data._id}`);
        } catch (error) {
            enqueueSnackbar(t('chatbotSettings.messages.createError', 'Erro ao criar chatbot'), { variant: 'error' });
        }
    };

    const handleDuplicate = async (id) => {
        try {
            const response = await duplicateChatbot(id);
            setChatbots([...chatbots, response.data.data]);
            enqueueSnackbar(t('chatbotSettings.messages.duplicateSuccess', 'Chatbot duplicado com sucesso'), { variant: 'success' });
        } catch (error) {
            enqueueSnackbar(t('chatbotSettings.messages.duplicateError', 'Erro ao duplicar chatbot'), { variant: 'error' });
        }
    };

    const handleDelete = async () => {
        if (!chatbotIdToDelete) return;
        try {
            await deleteChatbot(chatbotIdToDelete);
            setChatbots(chatbots.filter((c) => c._id !== chatbotIdToDelete));
            enqueueSnackbar(t('chatbotSettings.messages.deleteSuccess', 'Chatbot removido com sucesso'), { variant: 'success' });
            setOpenModalConfirmacao(false);
            setChatbotIdToDelete(null);
        } catch (error) {
            enqueueSnackbar(t('chatbotSettings.messages.deleteError', 'Erro ao remover chatbot'), { variant: 'error' });
        }
    };

    return (
        <div className={D.root} style={{ fontFamily: D.fontStack }}>
            <ModalConfimacao
                open={openModalConfirmacao}
                onClose={() => {
                    setChatbotIdToDelete(null);
                    setOpenModalConfirmacao(false);
                }}
                mensagem={t('chatbotSettings.deleteConfirmMessage', 'Deseja mesmo excluir este chatbot?')}
                onConfirm={handleDelete}
            />

            <div className={D.stickyHeader}>
                <div className={D.container}>
                    <div className={D.topbar}>
                        <div className={D.topbarIdentity}>
                            <h1 className={D.title}>{t('chatbotSettings.header.title', 'Chatbots')}</h1>
                            <p className={D.subtitle}>{t('chatbotSettings.header.subtitle', 'Fluxos de conversa automáticos para o atendimento.')}</p>
                        </div>
                        <div className={D.topbarActions}>
                            <button type="button" onClick={handleCreate} className={D.primaryButton}>
                                {t('chatbotSettings.newChatbotLabel', 'Novo chatbot')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`${D.container} pt-6 ${D.pageBody}`}>
                <div className={D.stack}>
                    <div className={D.sectionHeader}>
                        <h2 className={D.sectionHeaderTitle}>{t('chatbotSettings.sectionTitle', 'Fluxos')}</h2>
                        <p className={D.sectionHeaderSub}>{t('chatbotSettings.sectionSub', 'Clique em um fluxo para abrir o editor.')}</p>
                    </div>
                    {loading ? (
                        <div className={D.grid}>
                            {Array(4).fill(0).map((_, index) => (
                                <SkeletonChatbotCard key={index} />
                            ))}
                        </div>
                    ) : (
                        <div className={D.grid}>
                            <CardNew onClick={handleCreate} />
                            {chatbots?.map((bot, index) => (
                                <ChatbotCard
                                    key={bot._id || index}
                                    chatbot={bot}
                                    onClick={() => navigate(`/dashboard/chatbots/builder/${bot._id}`)}
                                    onDuplicate={() => handleDuplicate(bot._id)}
                                    onDelete={() => {
                                        setChatbotIdToDelete(bot._id);
                                        setOpenModalConfirmacao(true);
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------

function ChatbotCard({ chatbot, onClick, onDelete, onDuplicate }) {
    const { t } = useTranslation('settings');
    const hasNodes = chatbot.flowData?.nodes?.length > 0;

    return (
        <div
            onClick={onClick}
            className={`${D.card} relative group min-h-[220px]`}
        >
            <div className="absolute top-3.5 right-3.5 flex items-center gap-1.5 z-10">
                <span className={hasNodes ? D.badgeOk : D.badgeNeutral} />
                <button
                    type="button"
                    className={`${D.iconButton} opacity-0 group-hover:opacity-100`}
                    onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                    title={t('chatbotSettings.card.duplicateTooltip', 'Duplicar chatbot')}
                >
                    <Iconify icon="eva:copy-outline" width={16} />
                </button>
                <button
                    type="button"
                    className={`${D.iconButtonDanger} opacity-0 group-hover:opacity-100`}
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    title={t('chatbotSettings.card.deleteTooltip', 'Excluir chatbot')}
                >
                    <Iconify icon="eva:trash-2-outline" width={16} />
                </button>
            </div>

            <div className="flex-1 flex flex-col items-center pt-2">
                <h3 className={D.cardTitle} title={chatbot.name}>{chatbot.name}</h3>
                <p className={D.cardMeta} title={chatbot.description}>
                    {chatbot.description || t('chatbotSettings.card.noDescription', 'Sem descrição')}
                </p>
                <div className="flex-1 w-full flex flex-col justify-end gap-3 mt-4">
                    <div className="h-px bg-[#f0f0f3] w-full" />
                    <p className="text-[12px] text-[#71717a] text-center">
                        {hasNodes
                            ? t('chatbotSettings.card.nodesCount', '{{count}} nós no fluxo', { count: chatbot.flowData?.nodes?.length || 0 })
                            : t('chatbotSettings.card.emptyFlow', 'Fluxo vazio')}
                    </p>
                    <button type="button" className={D.ghostButton}>
                        {hasNodes ? t('chatbotSettings.card.configure', 'Configurar') : t('chatbotSettings.card.editFlow', 'Editar fluxo')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------

const CardNew = ({ onClick }) => {
    const { t } = useTranslation('settings');
    return (
        <button type="button" onClick={onClick} className={D.newCard}>
            <h3 className={D.newCardTitle}>{t('chatbotSettings.newChatbotLabel', 'Novo chatbot')}</h3>
            <p className={D.newCardSub}>{t('chatbotSettings.card.newSubtitle', 'Crie um novo fluxo de conversação')}</p>
        </button>
    );
};

// ----------------------------------------------------------------------

function SkeletonChatbotCard() {
    return (
        <div className="bg-white rounded-[12px] border border-[#e7e7ec] p-[22px] h-[220px] animate-pulse" />
    );
}